// src/components/ClientsTable.jsx
import React, { useState, useMemo, useEffect } from "react";
import Paginador from "./Paginador";

const headerStyle = { cursor: "pointer", whiteSpace: "nowrap" };

// helpers para ordenar IDs C-/A-/P-####
const getNumericPart = (id) => {
  const m = String(id || "")
    .toUpperCase()
    .match(/^[CPA]-(\d+)$/);
  return m ? parseInt(m[1], 10) : Number.POSITIVE_INFINITY;
};
const prefRank = (id) => {
  const c = String(id || "").toUpperCase()[0];
  return c === "C" ? 0 : c === "P" ? 1 : c === "A" ? 2 : 3;
};

const ClientsTable = ({ clientes = [], onEditClient, onDeleteClient }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const requestSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const sortedClientes = useMemo(() => {
    if (!Array.isArray(clientes)) return [];
    if (!sortConfig.key) return clientes;

    const { key, direction } = sortConfig;
    const dir = direction === "asc" ? 1 : -1;

    return [...clientes].sort((a, b) => {
      const va = a?.[key];
      const vb = b?.[key];

      // Columna ID: ordena por prefijo (C,P,A) y luego por número
      if (key === "id") {
        const ra = prefRank(va);
        const rb = prefRank(vb);
        if (ra !== rb) return (ra - rb) * dir;
        const na = getNumericPart(va);
        const nb = getNumericPart(vb);
        if (na === nb) return 0;
        return (na > nb ? 1 : -1) * dir;
      }

      // Para el resto de columnas:
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;

      const na =
        typeof va === "number" ? va : Number.isFinite(+va) ? +va : null;
      const nb =
        typeof vb === "number" ? vb : Number.isFinite(+vb) ? +vb : null;
      if (na != null && nb != null)
        return (na === nb ? 0 : na > nb ? 1 : -1) * dir;

      const sa = String(va).toLocaleLowerCase();
      const sb = String(vb).toLocaleLowerCase();
      return sa.localeCompare(sb) * dir;
    });
  }, [clientes, sortConfig]);

  const sortArrow = (key) =>
    sortConfig.key === key
      ? sortConfig.direction === "asc"
        ? " ▲"
        : " ▼"
      : "";

  // === Paginación (igual que en ListaTodos) ===
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const totalClientes = sortedClientes.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const paginatedClientes = useMemo(
    () => sortedClientes.slice(startIndex, endIndex),
    [sortedClientes, startIndex, endIndex]
  );

  const handleItemsPerPageChange = (items) => setItemsPerPage(items);
  const handlePageChange = (page) => setCurrentPage(page);

  useEffect(() => {
    setCurrentPage(1);
  }, [clientes, sortConfig, itemsPerPage]);

  return (
    <div className="tabla-cotizaciones-wrapper">
      <div className="tabla-cotizaciones-content">
        <table>
          <thead>
            <tr>
              {/* Nueva columna ID con sort */}
              <th style={headerStyle} onClick={() => requestSort("id")}>
                ID{sortArrow("id")}
              </th>
              <th style={headerStyle} onClick={() => requestSort("empresa")}>
                Nombre{sortArrow("empresa")}
              </th>
              <th style={headerStyle} onClick={() => requestSort("rfc")}>
                RFC{sortArrow("rfc")}
              </th>
              <th style={headerStyle} onClick={() => requestSort("telefono")}>
                Teléfono{sortArrow("telefono")}
              </th>
              <th style={headerStyle} onClick={() => requestSort("correo")}>
                Correo Electrónico{sortArrow("correo")}
              </th>
              <th
                style={headerStyle}
                onClick={() => requestSort("cuentasCobrar")}
              >
                Cuentas por Cobrar{sortArrow("cuentasCobrar")}
              </th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {paginatedClientes.map((cliente) => {
              const isCliente = (cliente?.tipo ?? "Cliente") === "Cliente";
              return (
                <tr key={cliente.__docId || cliente.id}>
                  {/* Solo mostramos ID si es de tipo Cliente */}
                  <td style={{ width: "10%" }}>
                    {isCliente ? cliente.id || "-" : ""}
                  </td>
                  <td style={{ width: "30%" }}>
                    {cliente.empresa || "Sin nombre"}
                  </td>
                  <td>{cliente.rfc || "Sin RFC"}</td>
                  <td>{cliente.telefono || "Sin teléfono"}</td>
                  <td>{cliente.correo || "Sin correo"}</td>
                  <td>{cliente.cuentasCobrar ?? "Sin datos"}</td>
                  <td
                    style={{
                      width: 80,
                      textAlign: "center",
                      padding: "4px 2px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <i
                      className="bi bi-pencil-fill"
                      style={{ cursor: "pointer", marginRight: 8 }}
                      title="Editar cliente"
                      onClick={() => onEditClient?.(cliente.__docId)}
                    />
                    <i
                      className="bi bi-trash-fill"
                      style={{ cursor: "pointer", color: "#dc3545" }}
                      title="Eliminar cliente"
                      onClick={() => onDeleteClient?.(cliente.__docId)}
                    />
                  </td>
                </tr>
              );
            })}

            {totalClientes === 0 && (
              <tr>
                {/* +1 columna por el nuevo ID */}
                <td colSpan={7} style={{ textAlign: "center", padding: 12 }}>
                  No hay clientes para mostrar.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {totalClientes > 0 && paginatedClientes.length === 0 && (
          <div className="no-resultados" style={{ padding: 12 }}>
            No hay clientes en esta página.
          </div>
        )}
      </div>

      <div className="tabla-cotizaciones-footer">
        <div className="paginador-container">
          <Paginador
            totalItems={totalClientes}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default ClientsTable;
